import { Dictionary, entries, get, upperFirst } from '@direct-frontend/stdlib';

import { $internals, FormActionsMap, FormInstance } from '../form/types';
import { FormNotInitializedError } from '../form/utils/assertFormInitialized';
import { getFormStoreSingleton } from '../store/store';
import { getFieldPathById } from '../store/utils/getFieldPathByFieldId';
import { BaseValidationError } from '../types';

import { BaseField, BaseFieldSelectorsMap, BaseFields } from './types';

/**
 * Привязывает селекторы поля к его текущему значение, и создает селектор-геттер и хук для каждого селектора из декларации
 * !IMPORTANT: состояние fieldsState не должно передаваться во внешние хелперы, чтобы не создавались неочевидные зависимости
 */
export function bindFieldSelectors<
    Field extends BaseField,
    FormName extends string,
    FormFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FormActions extends FormActionsMap<FormFields, FieldValidationError, FieldContext>,
    FieldContextInput,
    FieldContext,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FieldValidationError,
        FieldContextInput,
        FieldContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    field: Field,
    fieldId: string
) {
    if (!('selectors' in field) || !field.selectors) {
        return;
    }

    const { createSelector } = getFormStoreSingleton(form.name);

    // Приводим тип к базовому, потому что функция работает с базовыми типами полей, в которых используется any
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const fixedFieldSelectors = field.selectors as BaseFieldSelectorsMap;

    // Используем unknown так как тип bound селекторов приводится в getInstance, а тут он нам не важен
    const boundSelectors: Dictionary<unknown> = {};

    for (const [selectorKey, selector] of entries(fixedFieldSelectors)) {
        const [hook, getter] = createSelector((state, ...args: unknown[]) => {
            const formInitializationId = form[$internals].formInitializationId;

            const formState = state.forms[formInitializationId];

            if (!formState) {
                throw new FormNotInitializedError('Form not initialized', {
                    cause: null,
                    additional: {
                        name: form.name,
                        id: formInitializationId,
                    },
                });
            }

            const fieldValue = get(
                formState.value,
                getFieldPathById(() => formState.fieldsState, fieldId)
            );

            return selector(fieldValue, ...args);
        });

        boundSelectors[selectorKey] = getter;
        boundSelectors[`use${upperFirst(selectorKey)}`] = hook;
    }

    return boundSelectors;
}
